import { AnalyticsContextType } from "@/Contexts/AnalyticsContext";
import {
    CookieManagerContext,
    CookieSettings,
    initialCookieContext,
} from "@/Contexts/CookieManagerContext";
import { getCookie, setCookie } from "@/utils/CookieJar";
import { queryClient } from "@/utils/queryClient";
import { logger } from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { PropsWithChildren, useEffect, useState } from "react";

export function CookieManagementProvider({ children }: PropsWithChildren) {
    const { mutate: persistToServer } = useMutation(
        {
            mutationFn: ({ analytics, advertising }: CookieSettings) =>
                axios.post(route("cookie-settings"), {
                    analytics,
                    advertising,
                }),
            // RequiredProviders sit above every error boundary, so a thrown error would blank the whole app.
            throwOnError: false,
            onError: (error) =>
                logger.error("Failed to save cookie settings:", { error }),
        },
        queryClient,
    );

    const [cookieSettings, setCookieSettings] = useState(
        initialCookieContext.cookieSettings,
    );
    const [showCookieSettingsModal, setShowCookieSettingsModal] = useState(
        initialCookieContext.showCookieSettingsModal,
    );
    const [showCookieBanner, setShowCookieBanner] = useState(
        initialCookieContext.showCookieBanner,
    );
    const [hasEverBeenSaved, setHasEverBeenSaved] = useState(
        initialCookieContext.hasEverBeenSaved,
    );
    const [analyticsSettingToggleState, setAnalyticsToggleState] = useState(
        initialCookieContext.cookieSettings.analytics,
    );
    const [advertisingSettingToggleState, setAdvertisingToggleState] = useState(
        initialCookieContext.cookieSettings.advertising,
    );

    const saveCookieSettingsAndCloseAll = (
        cookieSettings: CookieSettings,
        analytics: AnalyticsContextType,
    ) => {
        analytics.trackEvent("saved_cookies", {
            analytics_cookie: cookieSettings.analytics,
            advertising_cookie: cookieSettings.advertising,
        });
        dataLayer.push({
            event: "saved_cookies",
            analytics_cookie: cookieSettings.analytics,
            advertising_cookie: cookieSettings.advertising,
        });
        setCookieSettings(cookieSettings);
        setCookie("cookie-settings", cookieSettings);
        setShowCookieSettingsModal(false);
        setShowCookieBanner(false);
        persistToServer(cookieSettings);
        setCookie("hasEverBeenSaved", true);
        setHasEverBeenSaved(true);
    };

    const resetCookieSettingsToLastSaved = () => {
        setAnalyticsToggleState(cookieSettings.analytics);
        setAdvertisingToggleState(cookieSettings.advertising);
    };

    useEffect(() => {
        if (getCookie("cookie-settings", null) !== null) return;
        setCookie("cookie-settings", {
            analytics: true,
            advertising: false,
        });
    }, []);

    return (
        <CookieManagerContext.Provider
            value={{
                showCookieSettingsModal,
                showCookieBanner,
                cookieSettings,
                resetCookieSettingsToLastSaved,
                saveCookieSettingsAndCloseAll,
                setShowCookieSettingsModal,
                setShowCookieBanner,
                hasEverBeenSaved,
                analyticsSettingToggleState,
                advertisingSettingToggleState,
                setAnalyticsToggleState,
                setAdvertisingToggleState,
            }}
        >
            {children}
        </CookieManagerContext.Provider>
    );
}
